/**
 * 환경 감지 유틸리티
 * 브라우저/WebView/네이티브 앱 환경을 감지하는 중앙화된 함수들
 */

/**
 * 브라우저 환경인지 확인
 * SSR 환경에서 window 객체 접근 전 체크에 사용
 */
export const isBrowser = (): boolean => {
  return typeof window !== 'undefined';
};

/**
 * React Native WebView 내에서 실행 중인지 확인
 * - 주 검증: window.ReactNativeWebView 객체 존재 여부
 * - 보조 검증: User-Agent에 'ReactNative' 포함 여부
 */
export const isNativeApp = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  // 주 검증: ReactNativeWebView 인터페이스 존재 확인
  const hasWebViewInterface = window.ReactNativeWebView !== undefined;

  // 보조 검증: User-Agent 확인 (스푸핑 방지용 추가 레이어)
  const userAgent = navigator.userAgent || '';
  const hasReactNativeUserAgent = userAgent.includes('ReactNative');

  // 둘 중 하나라도 true면 네이티브 앱으로 판단
  // ReactNativeWebView가 있으면 확실히 WebView 환경
  return hasWebViewInterface || hasReactNativeUserAgent;
};

/**
 * 순수 웹 브라우저 환경인지 확인 (WebView가 아닌)
 */
export const isPureWebBrowser = (): boolean => {
  return isBrowser() && !isNativeApp();
};

/**
 * iOS WebView 환경인지 확인
 */
export const isIOSWebView = (): boolean => {
  if (!isNativeApp()) {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(userAgent);
};

/**
 * Android WebView 환경인지 확인
 */
export const isAndroidWebView = (): boolean => {
  if (!isNativeApp()) {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return /Android/.test(userAgent);
};

/**
 * 모바일 기기인지 확인 (카메라 촬영 옵션 노출 등에 사용).
 * - 네이티브 앱(WebView)이면 true
 * - 일반 브라우저는 User-Agent로 모바일 여부 판별 (모바일 브라우저는 카메라 촬영 input capture 지원)
 */
export const isMobileDevice = (): boolean => {
  if (!isBrowser()) {
    return false;
  }
  if (isNativeApp()) {
    return true;
  }
  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(userAgent);
};

/**
 * 플랫폼 타입
 */
export type Platform = 'ios' | 'android' | 'web';

/**
 * 현재 플랫폼 반환
 * WebView 환경에서는 iOS/Android를, 일반 브라우저에서는 'web' 반환
 */
export const getPlatform = (): Platform => {
  if (isIOSWebView()) {
    return 'ios';
  }
  if (isAndroidWebView()) {
    return 'android';
  }
  return 'web';
};
